import { useEffect, useMemo, useState } from "react";
import { ScrollView, Text, View, useWindowDimensions } from "react-native";
import ReAnimated, { FadeIn, LinearTransition } from "react-native-reanimated";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Check, ChevronLeft, Plus } from "lucide-react-native";
import { useReducedMotion } from "@src/packages/moon-ui/hooks/useReducedMotion";
import {
  ActionBanner,
  Dialog,
  DialogActions,
  DialogHeader,
  EMBER,
  IconButton,
  MetaText,
  DateField,
  MoonSearchInput,
  PrimaryButton,
  Row,
  SectionTitleRow,
  Tag,
  TextActionButton,
  ListRowSkeleton,
} from "@louise/moon-ui";
import { useApiClient } from "@src/packages/api/louise-api";
import type { DataPointSampleType } from "@src/packages/content/types";
import type { DocumentProcessingRun } from "@src/packages/documents/ingestion/documentProcessingRun";
import {
  findDocumentValues,
  type DocumentFoundValue,
  type DocumentValueDraft,
} from "@src/packages/documents/ingestion/documentValues";
import {
  confirmDocumentValues,
  type DocumentValueToRecord,
} from "@src/packages/documents/ingestion/documentValueReview";
import { createUploadJobIdempotencyKey } from "@src/packages/documents/ingestion/uploadJobActivity";
import { useTranslations } from "@src/packages/i18n";
import { LabValueField } from "@src/packages/test-results/components/LabValueField";
import { useClinicalObservations } from "@src/packages/test-results/hooks/useClinicalObservations";
import {
  findLabTest,
  getLabTests,
  searchLabTests,
  type LabTest,
} from "@src/packages/test-results/labTestCatalogue";
import { getCanonicalObservationValue } from "@src/packages/test-results/normalized-results";
import { useDateFormat } from "@src/packages/utils/useDateFormat";

/** A value already in the record, shown as it was saved. */
export type DocumentSavedValue = {
  id: string;
  name: string;
  value: string;
  unit: string;
  date: string | null;
};

type SheetState =
  | { kind: "search" }
  | {
      kind: "value";
      test: LabTest | null;
      name: string;
      /** The found value or added value being edited; null while adding. */
      editing:
        | { kind: "found"; claimId: string; draft: DocumentValueDraft }
        | { kind: "added"; clientRequestId: string; draft: DocumentValueDraft }
        | null;
    };

function todayIsoDate(): string {
  const now = new Date();
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/** The value with its unit, the unit a step quieter. */
function ValueWithUnit({ value, unit }: { value: string; unit: string }) {
  return (
    <Text
      className="font-dm-sans"
      numberOfLines={1}
      style={{ fontSize: 15, fontWeight: "700", color: EMBER.colors.text }}
    >
      {value}
      {unit ? (
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 12,
            fontWeight: "500",
            color: EMBER.colors.textLight,
          }}
        >
          {` ${unit}`}
        </Text>
      ) : null}
    </Text>
  );
}

function ValueSheet({
  state,
  tests,
  documentName,
  documentDate,
  onClose,
  onBackToSearch,
  onPickTest,
  onSave,
  onRemove,
}: {
  state: SheetState | null;
  tests: LabTest[];
  documentName: string;
  documentDate: string | null;
  onClose: () => void;
  onBackToSearch: () => void;
  onPickTest: (test: LabTest | null, name: string) => void;
  onSave: (draft: DocumentValueDraft) => void;
  onRemove: () => void;
}) {
  const t = useTranslations();
  const { height } = useWindowDimensions();
  const dialogMaxHeight = Math.min(720, Math.max(420, height - 64));
  const [query, setQuery] = useState("");
  const [value, setValue] = useState("");
  const [unit, setUnit] = useState("");
  const [date, setDate] = useState("");

  const editing = state?.kind === "value" ? state.editing : null;
  const valueState = state?.kind === "value" ? state : null;

  useEffect(() => {
    if (!state) return;
    setQuery("");
    if (state.kind !== "value") return;
    setValue(state.editing?.draft.value ?? "");
    setUnit(state.editing?.draft.unit ?? state.test?.units[0] ?? "");
    setDate(state.editing?.draft.date ?? documentDate ?? todayIsoDate());
  }, [state, documentDate]);

  const matches = useMemo(() => searchLabTests(tests, query), [tests, query]);
  const typedName = query.trim();
  const offersTypedName =
    typedName.length > 0 && !findLabTest(tests, typedName);

  const unitsLabel = (units: readonly string[]) =>
    units.length === 2
      ? t("documents.found_values.one_unit_or_the_other", {
          params: { first: units[0] ?? "", second: units[1] ?? "" },
        })
      : units.join(", ");

  return (
    <Dialog
      containerStyle={{ maxHeight: dialogMaxHeight }}
      visible={state !== null}
      onClose={onClose}
      closeButton="minimal"
      surfaceStyle={{ maxHeight: dialogMaxHeight }}
      contentStyle={{
        paddingTop: 24,
        paddingHorizontal: 24,
        paddingBottom: 24,
        maxHeight: dialogMaxHeight,
      }}
      testID="document-value-sheet"
    >
      {state?.kind === "search" ? (
        <>
          <DialogHeader
            title={t("documents.found_values.add_a_value")}
            subtitle={t("documents.found_values.which_test_is_it")}
            style={{ marginBottom: 14 }}
          />
          <MoonSearchInput
            value={query}
            onChangeText={setQuery}
            onClear={() => setQuery("")}
            clearAction="document_value_clear_test_search"
            placeholder={t("documents.found_values.search_tests")}
            accessibilityLabel={t("documents.found_values.search_tests")}
            testID="document-value-test-search"
          />
          <ScrollView
            showsVerticalScrollIndicator={false}
            keyboardShouldPersistTaps="handled"
            style={{ flexShrink: 1, marginTop: 12 }}
            contentContainerStyle={{ gap: 8, paddingBottom: 4 }}
          >
            {offersTypedName ? (
              <Row
                surface="input"
                density="dense"
                title={t("documents.found_values.add_test_named", {
                  params: { name: typedName },
                })}
                leading={
                  <Plus
                    size={16}
                    color={EMBER.colors.textMid}
                    strokeWidth={2}
                  />
                }
                action="document_value_pick_typed_test"
                onPress={() => onPickTest(null, typedName)}
              />
            ) : null}
            {matches.map((test) => (
              <Row
                key={test.id}
                surface="input"
                density="dense"
                title={test.title}
                subtitle={unitsLabel(test.units) || undefined}
                subtitleVariant="meta"
                action="document_value_pick_test"
                onPress={() => onPickTest(test, test.title)}
              />
            ))}
          </ScrollView>
        </>
      ) : valueState ? (
        <>
          <DialogHeader
            // The way back to the tests sits in the title row, level with
            // the close.
            leading={
              editing ? null : (
                <IconButton
                  accessibilityLabel={t("documents.upload.back")}
                  action="document_value_back_to_tests"
                  onPress={onBackToSearch}
                  hitSlop={8}
                  size="sm"
                  pressedOpacity={1}
                >
                  <ChevronLeft
                    size={20}
                    color={EMBER.colors.textLight}
                    strokeWidth={2}
                  />
                </IconButton>
              )
            }
            title={valueState.name}
            subtitle={t("documents.found_values.in_document", {
              params: { name: documentName },
            })}
            style={{ marginBottom: 16 }}
          />
          <View style={{ gap: 14 }}>
            <LabValueField
              label={t("documents.found_values.value_of_the_test")}
              value={value}
              onChangeValue={setValue}
              unit={unit}
              onChangeUnit={setUnit}
              marker={valueState.name}
              units={valueState.test?.units}
              numeric
              placeholder="0.0"
              unitAction="document_value_unit"
              testID="document-value-input"
            />
            <DateField
              label={t("documents.upload.fields.date")}
              value={date}
              onChange={setDate}
            />
          </View>
          <DialogActions style={{ marginTop: 20 }}>
            <PrimaryButton
              label={
                editing
                  ? t("documents.found_values.save_value")
                  : t("documents.found_values.add_this_value")
              }
              action="document_value_save"
              disabled={value.trim().length === 0}
              onPress={() =>
                onSave({
                  name: valueState.name,
                  value: value.trim(),
                  unit: unit.trim(),
                  date: date || null,
                })
              }
              fullWidth
            />
          </DialogActions>
          {editing ? (
            <TextActionButton
              label={t("documents.found_values.remove_this_value")}
              action="document_value_remove"
              tone="destructive"
              size="sm"
              centered
              onPress={onRemove}
              style={{ marginTop: 14 }}
            />
          ) : null}
        </>
      ) : null}
    </Dialog>
  );
}

export type DocumentValuesReviewProps = {
  /** The document's processing run, or null when it was never read. */
  run: DocumentProcessingRun | null;
  /**
   * The document's evidence entity, which values added here link to. Null
   * for a document no upload job produced: the API exposes no way to link an
   * observation to it, so `onSaveUnlinkedValues` saves those instead.
   */
  sourceEntityId: string | null;
  loading?: boolean;
  /** The file is saved but the reading has not finished yet. */
  stillReading?: boolean;
  documentName: string;
  documentDate: string | null;
  /** Results saved against the document before it was read. */
  earlierValues?: DocumentSavedValue[];
  /** Which tests "Add a value" offers; every test when omitted. */
  sampleTypes?: readonly DataPointSampleType[] | null;
  isReadOnly?: boolean;
  onSaveUnlinkedValues?: (values: DocumentValueDraft[]) => Promise<void>;
  onConfirmed?: (savedCount: number) => void;
  onNotNow?: () => void;
};

/**
 * The values found in a document, to confirm before they are saved: one row
 * per value with the figure where a chevron would be. A value opens a sheet
 * to correct or remove it, "Add a value" searches the test catalogue first,
 * and one "Confirm and save" keeps them all.
 */
export function DocumentValuesReview({
  run,
  sourceEntityId,
  loading = false,
  stillReading = false,
  documentName,
  documentDate,
  earlierValues = [],
  sampleTypes,
  isReadOnly = false,
  onSaveUnlinkedValues,
  onConfirmed,
  onNotNow,
}: DocumentValuesReviewProps) {
  const t = useTranslations();
  const { abbrev } = useDateFormat();
  const queryClient = useQueryClient();
  const { client, patientId } = useApiClient();
  const { data: observations } = useClinicalObservations();
  const reducedMotion = useReducedMotion();
  const [currentRun, setCurrentRun] = useState(run);
  const [edits, setEdits] = useState<Record<string, DocumentValueDraft>>({});
  const [removed, setRemoved] = useState<ReadonlySet<string>>(new Set());
  const [added, setAdded] = useState<DocumentValueToRecord[]>([]);
  const [sheet, setSheet] = useState<SheetState | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    setCurrentRun(run);
  }, [run]);

  const tests = useMemo(() => getLabTests(sampleTypes), [sampleTypes]);

  const found = useMemo<DocumentFoundValue[]>(
    () => (currentRun ? findDocumentValues(currentRun, documentDate) : []),
    [currentRun, documentDate],
  );
  const waiting = found.filter((value) => !value.saved);
  const shownWaiting = waiting.filter((value) => !removed.has(value.claimId));
  const savedFound = found.filter((value) => value.saved);

  const recordedValues = useMemo<DocumentSavedValue[]>(() => {
    if (!sourceEntityId || !observations) return [];
    return observations
      .filter((observation) =>
        observation.evidence.some(
          (link) => link.evidenceEntityId === sourceEntityId,
        ),
      )
      .map((observation) => ({
        id: observation.id,
        name: observation.rawLabel,
        value: getCanonicalObservationValue(observation),
        unit: observation.originalUnit ?? observation.normalizedUnit ?? "",
        date: observation.effectiveAt.slice(0, 10),
      }));
  }, [observations, sourceEntityId]);

  const savedValues: DocumentSavedValue[] = [
    ...savedFound.map((value) => ({
      id: value.claimId,
      name: value.name,
      value: value.value,
      unit: value.unit,
      date: value.date,
    })),
    ...recordedValues,
    ...earlierValues,
  ];
  const needsConfirmation = waiting.length > 0 || added.length > 0;
  const allSaved = !needsConfirmation && savedValues.length > 0;
  const rowCount = shownWaiting.length + savedValues.length + added.length;

  const confirm = useMutation({
    mutationFn: async () => {
      const savedCount = waiting.length - removed.size + added.length;
      if (!sourceEntityId) {
        await onSaveUnlinkedValues?.(added);
        setAdded([]);
        return { run: currentRun, savedCount };
      }
      if (!patientId) {
        throw new Error("Saving a document's values needs a patient");
      }
      const nextRun = await confirmDocumentValues(client, patientId, {
        runId: currentRun?.id ?? null,
        sourceEntityId,
        documentDate,
        edits,
        removedClaimIds: removed,
        recorded: added,
        onReviewed: (corrections, reviewedRun) => {
          // The reviewed values now read from the run; corrections wait with
          // the added values until they are recorded.
          setCurrentRun(reviewedRun);
          setAdded((current) => [...current, ...corrections]);
          setEdits({});
          setRemoved(new Set());
        },
        onRecorded: (clientRequestId) =>
          setAdded((current) =>
            current.filter(
              (value) => value.clientRequestId !== clientRequestId,
            ),
          ),
      });
      return { run: nextRun, savedCount };
    },
    onMutate: () => setErrorMessage(null),
    onSuccess: ({ run: nextRun, savedCount }) => {
      if (nextRun) setCurrentRun(nextRun);
      void queryClient.invalidateQueries({
        queryKey: ["clinical-observations"],
      });
      void queryClient.invalidateQueries({
        queryKey: ["document-processing-lookup"],
      });
      onConfirmed?.(savedCount);
    },
    onError: (error) => {
      console.warn("Could not save the document's values", error);
      setErrorMessage(t("documents.found_values.could_not_save_values"));
    },
  });

  const openFound = (value: DocumentFoundValue) => {
    const draft = edits[value.claimId] ?? {
      name: value.name,
      value: value.value,
      unit: value.unit,
      date: value.date,
    };
    setSheet({
      kind: "value",
      test: findLabTest(tests, draft.name) ?? null,
      name: draft.name,
      editing: { kind: "found", claimId: value.claimId, draft },
    });
  };

  const openAdded = (value: DocumentValueToRecord) => {
    setSheet({
      kind: "value",
      test: findLabTest(tests, value.name) ?? null,
      name: value.name,
      editing: {
        kind: "added",
        clientRequestId: value.clientRequestId,
        draft: value,
      },
    });
  };

  const handleSave = (draft: DocumentValueDraft) => {
    const editing = sheet?.kind === "value" ? sheet.editing : null;
    if (editing?.kind === "found") {
      setEdits((current) => ({ ...current, [editing.claimId]: draft }));
    } else if (editing?.kind === "added") {
      setAdded((current) =>
        current.map((value) =>
          value.clientRequestId === editing.clientRequestId
            ? { ...draft, clientRequestId: value.clientRequestId }
            : value,
        ),
      );
    } else {
      setAdded((current) => [
        ...current,
        { ...draft, clientRequestId: createUploadJobIdempotencyKey() },
      ]);
    }
    setSheet(null);
  };

  const handleRemove = () => {
    const editing = sheet?.kind === "value" ? sheet.editing : null;
    if (editing?.kind === "found") {
      setRemoved((current) => new Set([...current, editing.claimId]));
    } else if (editing?.kind === "added") {
      setAdded((current) =>
        current.filter(
          (value) => value.clientRequestId !== editing.clientRequestId,
        ),
      );
    }
    setSheet(null);
  };

  const dateLabel = (date: string | null) =>
    date ? abbrev(date) : t("documents.no_date");

  return (
    <View testID="document-values-section">
      <SectionTitleRow
        title={t("documents.found_values.values_found_in_this_document")}
        right={
          allSaved ? (
            <Tag
              label={t("documents.found_values.values_saved")}
              tone="green"
              icon={Check}
            />
          ) : null
        }
      />
      {loading ? (
        <View
          accessibilityRole="progressbar"
          accessibilityLabel={t("documents.processing_review.loading_meaning")}
        >
          {[0, 1].map((row) => (
            <ListRowSkeleton key={row} showSubtitle showTrailing />
          ))}
        </View>
      ) : rowCount === 0 ? (
        <MetaText style={{ marginTop: 0 }}>
          {t(
            stillReading
              ? "documents.processing_review.meaning_not_ready"
              : "documents.found_values.no_values_found",
          )}
        </MetaText>
      ) : (
        <View style={{ gap: 8 }}>
          {shownWaiting.map((value, idx) => {
            const shown = edits[value.claimId] ?? value;
            const entering = reducedMotion
              ? undefined
              : FadeIn.delay(Math.min(idx * 40, 400)).duration(220);
            return (
              <ReAnimated.View
                key={value.claimId}
                entering={entering}
                layout={
                  reducedMotion ? undefined : LinearTransition.springify()
                }
              >
                <Row
                  surface="input"
                  title={shown.name}
                  subtitle={dateLabel(shown.date)}
                  subtitleVariant="meta"
                  trailing={
                    <ValueWithUnit value={shown.value} unit={shown.unit} />
                  }
                  chevron={false}
                  action="document_value_edit"
                  accessibilityLabel={t("documents.found_values.edit_value", {
                    params: { name: shown.name },
                  })}
                  onPress={isReadOnly ? undefined : () => openFound(value)}
                  testID="document-value-row"
                />
              </ReAnimated.View>
            );
          })}
          {savedValues.map((value, idx) => {
            const offset = shownWaiting.length + idx;
            const entering = reducedMotion
              ? undefined
              : FadeIn.delay(Math.min(offset * 40, 400)).duration(220);
            return (
              <ReAnimated.View
                key={value.id}
                entering={entering}
                layout={
                  reducedMotion ? undefined : LinearTransition.springify()
                }
              >
                <Row
                  surface="input"
                  title={value.name}
                  subtitle={dateLabel(value.date)}
                  subtitleVariant="meta"
                  trailing={
                    <ValueWithUnit value={value.value} unit={value.unit} />
                  }
                  chevron={false}
                  testID="document-saved-value-row"
                />
              </ReAnimated.View>
            );
          })}
          {added.map((value, idx) => {
            const offset = shownWaiting.length + savedValues.length + idx;
            const entering = reducedMotion
              ? undefined
              : FadeIn.delay(Math.min(offset * 40, 400)).duration(220);
            return (
              <ReAnimated.View
                key={value.clientRequestId}
                entering={entering}
                layout={
                  reducedMotion ? undefined : LinearTransition.springify()
                }
              >
                <Row
                  surface="input"
                  title={value.name}
                  subtitle={dateLabel(value.date)}
                  subtitleVariant="meta"
                  trailing={
                    <ValueWithUnit value={value.value} unit={value.unit} />
                  }
                  chevron={false}
                  action="document_value_edit_added"
                  accessibilityLabel={t("documents.found_values.edit_value", {
                    params: { name: value.name },
                  })}
                  onPress={isReadOnly ? undefined : () => openAdded(value)}
                  testID="document-value-row"
                />
              </ReAnimated.View>
            );
          })}
        </View>
      )}
      {!isReadOnly && !loading ? (
        <TextActionButton
          label={t("documents.found_values.add_a_value")}
          action="document_value_add"
          size="sm"
          icon={Plus}
          onPress={() => setSheet({ kind: "search" })}
          style={{ alignSelf: "flex-start", marginTop: 12, marginLeft: 4 }}
          testID="document-value-add"
        />
      ) : null}

      {errorMessage ? (
        <ActionBanner
          variant="important"
          message={errorMessage}
          style={{ marginTop: 16 }}
        />
      ) : null}

      {needsConfirmation && !isReadOnly ? (
        <View style={{ marginTop: 22, gap: 12 }}>
          <PrimaryButton
            label={t("documents.found_values.confirm_and_save")}
            action="document_values_confirm"
            loading={confirm.isPending}
            disabled={confirm.isPending}
            onPress={() => confirm.mutate()}
            fullWidth
          />
          {onNotNow ? (
            <TextActionButton
              label={t("documents.found_values.not_now")}
              action="document_values_not_now"
              size="sm"
              centered
              onPress={onNotNow}
            />
          ) : null}
        </View>
      ) : null}

      <ValueSheet
        state={sheet}
        tests={tests}
        documentName={documentName}
        documentDate={documentDate}
        onClose={() => setSheet(null)}
        onBackToSearch={() => setSheet({ kind: "search" })}
        onPickTest={(test, name) =>
          setSheet({ kind: "value", test, name, editing: null })
        }
        onSave={handleSave}
        onRemove={handleRemove}
      />
    </View>
  );
}

export default DocumentValuesReview;
